import {Component, OnInit, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {
  IUser,
  User,
  UserAuthorities,
  UserAuthoritiesEnum,
  UserStatus,
  UserStatusEnum
} from '../../shared/model/user.model';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {AdminService} from '../../services/admin.service';
import {INPUT_LIMITS} from '../../shared/constants/input-limits';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MessageService} from '../../shared/components/message.service';
import {ModalComponent} from '../../shared/components/modal/modal.component';
import {ModalService} from '../../shared/components/modal/modal.service';
import {MatPaginatorIntl, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {A11yModule} from '@angular/cdk/a11y';
import {MatPaginatorI18nService} from '../../services/material/mat-paginator-i18n.service';
import {Badge} from '@spirit-testing/component-library';
import {Advisor} from '../../shared/model/advisor.model';
import {AdvisorService} from '../../services/advisor.service';
import {loadAllPages} from '../../shared/load-all-pages';
import {Router} from '@angular/router';
import {NgClass, NgStyle} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import { catchError, finalize, forkJoin, of, switchMap } from 'rxjs';
import {CompanyService} from '../../services/company.service';
import {BenchOverviewService} from '../../services/bench-overview.service';
import {Company, CompanyFilter} from '../../shared/model/company.model';
import {BusinessUnitDTO} from '../../shared/model/bench-entry.model';
import {CompaniesModalComponent} from './companies-modal/companies-modal.component';
import {BuCatalogModalComponent} from './bu-catalog-modal/bu-catalog-modal.component';
import {AdvisorsModalComponent} from './advisors-modal/advisors-modal.component';
import {CostCalculationModalComponent} from './cost-calculation-modal/cost-calculation-modal.component';
import {
  ADMIN_DASH_RETURN_MARKER,
  ADMIN_DASH_RETURN_QUERY_PARAM,
} from '../../shared/constants/cv-import-return';
import {sortIndicatorFor} from '../../shared/sort-indicator';
import {UserRoleService} from '../../services/userRole.service';
import {isNoFreeAbbreviationError} from '../../shared/errors/no-free-abbreviation';
import {invalidNameFields, NameField} from '../../shared/errors/constraint-violations';

const INVALID_NAME_MESSAGE_KEYS: Record<NameField, string> = {
  firstName: 'adminDash.adminModal.errors.firstNameInvalid',
  lastName: 'adminDash.adminModal.errors.lastNameInvalid',
};

type AdminCompanyFilter = CompanyFilter | 'NONE';

type AdminRoleFilter =
  | 'ALL'
  | UserAuthoritiesEnum.SALE
  | UserAuthoritiesEnum.ADMIN
  | UserAuthoritiesEnum.OFFICE;

// NP20-917: sortierbare Spalten der Benutzertabelle. Auswahl, Rollen (Multi-Value)
// und Aktionen sind bewusst nicht sortierbar.
type AdminSortKey =
  | 'user'
  | 'abbreviation'
  | 'email'
  | 'company'
  | 'account'
  | 'accepted'
  | 'advisor'
  | 'cost';

@Component({
  selector: 'app-admindash',
  imports: [
    TranslatePipe,
    FormsModule,
    MatButton,
    ModalComponent,
    MatPaginatorModule,
    ReactiveFormsModule,
    MatTooltip,
    NgClass,
    NgStyle,
    A11yModule,
    CompaniesModalComponent,
    BuCatalogModalComponent,
    AdvisorsModalComponent,
    CostCalculationModalComponent,
  ],
  templateUrl: './admindash.component.html',
  styleUrl: './admindash.component.scss',
  providers: [{provide: MatPaginatorIntl, useClass: MatPaginatorI18nService}],
  standalone: true
})
export class AdmindashComponent implements OnInit {
  protected get LIMITS() { return INPUT_LIMITS; }

  readonly user = signal('');
  showDeleteModal: boolean = false;
  userModalOpen = false;
  userModalMode: 'edit' | 'add' = 'edit';
  isAddingUser = false;
  userDraft: IUser | null = null;
  formFirma: string | null = null;
  selectedUser: IUser;
  users: IUser[] = [];
  filteredUsers: IUser[] = [];
  searchTerm: string = '';
  openOnly = false;
  advisorOptions: Advisor[] = [];
  pageSize = 10;
  pageIndex = 0;
  advisorMap = new Map<string, Advisor>();

  get companies(): readonly Company[] { return this.companyService.companies(); }
  selectedCompany: AdminCompanyFilter = 'ALL';
  selectedRole: AdminRoleFilter = 'ALL';
  readonly roleFilterOptions: readonly AdminRoleFilter[] = [
    UserAuthoritiesEnum.SALE,
    UserAuthoritiesEnum.ADMIN,
    UserAuthoritiesEnum.OFFICE,
  ];
  companiesModalOpen = false;
  advisorsModalOpen = false;
  buModalOpen = false;
  costCalculationModalOpen = false;
  selectedUserIds = new Set<string>();
  bulkAdvisorId = '';
  private companyIdToFirma = new Map<string, string>();
  businessUnits: BusinessUnitDTO[] = [];

  // NP20-917: aktive Sortier-Spalte. null bedeutet "keine Sortierung", die Liste
  // bleibt dann in der Reihenfolge, wie sie das Backend geliefert hat.
  sortKey: AdminSortKey | null = 'user';
  sortDir: 'asc' | 'desc' = 'asc';


  get paginatedUsers(): IUser[] {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredUsers.slice(start, end);
  }

  authorityOptions: UserAuthorities[] = Object.values(UserAuthoritiesEnum).map(
    (role) => ({name: role})
  );

  compareAuthorities(a: UserAuthorities, b: UserAuthorities): boolean {
    return a?.name === b?.name;
  }

  compareAdvisors(a: Advisor, b: Advisor): boolean {
    return a?.id === b?.id;
  }

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private modalService: ModalService,
    private advisorService: AdvisorService,
    private companyService: CompanyService,
    private benchOverviewService: BenchOverviewService,
    private router: Router,
    private translate: TranslateService,
    private userRoleService: UserRoleService
  ) {
  }

  /**
   * Rollen darf nur ein Admin aendern. Das Admin-Dashboard ist auch fuer Office
   * erreichbar — dort sind die Rollen deshalb nur noch lesbar, denn das Backend (NP20-947)
   * beantwortet jede Rollenaenderung durch Nicht-Admins mit 403.
   */
  get canEditRoles(): boolean {
    return this.userRoleService.hasRole(UserAuthoritiesEnum.ADMIN);
  }

  closeDeleteModal() {
    this.modalService.close('deleteUser');
    this.showDeleteModal = false;
  }

  ngOnInit() {
    this.loadBusinessUnits();
    // Advisors and companies must be loaded before the users, so both the advisor
    // (superVisor) and the firma label of the stored companyId can be attached in a single pass.
    forkJoin({
      // Alle Seiten: fehlt der Betreuer eines Benutzers in advisorMap, laesst sich der
      // Benutzer nicht mehr speichern — die Gueltigkeitspruefung findet ihn dann nicht.
      advisors: loadAllPages<Advisor>((page, size) => this.advisorService.getAdvisors(page, size), 200),
      companies: this.companyService.load(),
    }).subscribe({
      next: ({advisors, companies}) => {
        this.advisorOptions = advisors;
        this.advisorOptions.forEach(a => this.advisorMap.set(a.id, a));
        this.buildCompanyLookup(companies);
        this.loadAllUsers();
      },
      error: () => {
        // Keep the user table usable even if advisor/company lookups fail;
        // firma stays unresolved, so warn the user that the company column/filter is incomplete.
        this.messageService.warning('adminDash.userTable.companyLoadWarning');
        this.loadAllUsers();
      }
    });
  }

  private loadBusinessUnits(): void {
    this.benchOverviewService.getBusinessUnits()
      .pipe(catchError(() => of([] as BusinessUnitDTO[])))
      .subscribe(units => this.businessUnits = units);
  }

  get activeBuCount(): number {
    return this.businessUnits.filter(unit => unit.active).length;
  }

  private buildCompanyLookup(companies: Company[]): void {
    this.companyIdToFirma.clear();
    companies.forEach(company => {
      const abbr = this.normalizeAbbr(company.abbreviation);
      if (abbr) {
        this.companyIdToFirma.set(company.id, abbr);
      }
    });
  }

  /** companyIdToFirma legt gross ab, Chips und companyService liefern das rohe Kuerzel. */
  private normalizeAbbr(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed.toUpperCase() : null;
  }

  private firmaOf(user: IUser): string | null {
    return user.companyId ? this.companyIdToFirma.get(user.companyId) ?? null : null;
  }

  loadAllUsers(status?: string) {
    const size = 1000;
    const params: any = {};
    if (status) {
      params.status = status;
    }

    this.adminService.getUsersPage(params, 0, size).subscribe({
      next: (data) => {
        this.users = data.content.map(user => ({
          ...user,
          superVisor: this.advisorMap.get(user.superVisorId),
          firma: this.firmaOf(user)
        }));

        if (status === 'INACTIVE') {
          this.users = this.users.sort(
            (a, b) =>
              new Date(b.createdDate).getTime() -
              new Date(a.createdDate).getTime()
          );
        }

        this.applyFilter();
      },
      error: () => {
        this.messageService.error('adminDash.userTable.loadingError');
      }
    });
  }

  private isEmailValid(email?: string): boolean {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  openUserModal(mode: 'edit' | 'add', user?: IUser): void {
    this.userModalMode = mode;
    const base: IUser = user
      ? {...user, authorities: [...(user.authorities ?? [])]}
      : (new User() as IUser);
    if (mode === 'add') {
      base.langKey = base.langKey || 'de';
      base.status = base.status ?? UserStatusEnum.INACTIVE;
    }
    this.userDraft = base;
    this.formFirma = this.firmaAbbreviationOf(base) ?? null;
    this.userModalOpen = true;
  }

  closeUserModal(): void {
    this.userModalOpen = false;
    this.userDraft = null;
    this.formFirma = null;
  }

  private firmaAbbreviationOf(user: IUser): string | null {
    return user.companyId ? (this.companyService.byId(user.companyId)?.abbreviation ?? null) : null;
  }

  advisorsForFirma(): Advisor[] {
    if (!this.formFirma) return [];
    const company = this.companyService.byAbbreviation(this.formFirma);
    if (!company) return [];
    return this.advisorOptions.filter(a => this.advisorLeads(a, company.id));
  }

  /**
   * Zusaetzlich der eingetragene Betreuer, wenn er die gewaehlte Firma nicht (mehr) fuehrt:
   * sonst zeigt das Feld nur den Platzhalter und verschweigt, wer aktuell zustaendig ist.
   */
  advisorSelectOptions(): Advisor[] {
    const leading = this.advisorsForFirma();
    const current = this.draftAdvisor();
    return current && !leading.some(a => a.id === current.id)
      ? [...leading, current]
      : leading;
  }

  advisorLeadsSelectedFirma(advisor: Advisor): boolean {
    return this.advisorsForFirma().some(a => a.id === advisor.id);
  }

  private draftAdvisor(): Advisor | undefined {
    const draft = this.userDraft;
    if (!draft) return undefined;
    return draft.superVisor ?? (draft.superVisorId ? this.advisorMap.get(draft.superVisorId) : undefined);
  }

  private advisorLeads(advisor: Advisor | undefined, companyId: string | null | undefined): boolean {
    return !!companyId && !!advisor?.companyIds?.includes(companyId);
  }

  onFormFirmaChange(value: string | null): void {
    this.formFirma = value;
    // Betreuer nur zuruecksetzen, wenn er die gewaehlte Firma nicht fuehrt — ein
    // Betreuer mehrerer Firmen bleibt beim Firmenwechsel stehen.
    const draft = this.userDraft;
    if (!draft) return;
    const advisor = this.draftAdvisor();
    const targetCompanyId = value ? this.companyService.byAbbreviation(value)?.id : null;
    if (!this.advisorLeads(advisor, targetCompanyId)) {
      draft.superVisor = undefined;
      draft.superVisorId = undefined;
    }
  }

  onFormAdvisorChange(advisorId: string | null): void {
    const draft = this.userDraft;
    if (!draft) return;
    const advisor = advisorId ? this.advisorMap.get(advisorId) : undefined;
    draft.superVisor = advisor;
    draft.superVisorId = advisor?.id;
  }

  hasRole(role: UserAuthoritiesEnum): boolean {
    return !!this.userDraft?.authorities?.some(a => a.name === role);
  }

  toggleRole(role: UserAuthoritiesEnum): void {
    if (!this.userDraft || !this.canEditRoles) return;
    const auths = this.userDraft.authorities ?? [];
    const idx = auths.findIndex(a => a.name === role);
    this.userDraft.authorities = idx >= 0
      ? auths.filter((_, i) => i !== idx)
      : [...auths, {name: role}];
  }

  toggleAccepted(): void {
    if (!this.userDraft) return;
    this.userDraft.status = this.userDraft.status === UserStatusEnum.ACTIVE
      ? UserStatusEnum.INACTIVE
      : UserStatusEnum.ACTIVE;
  }

  readonly roleOrder: UserAuthoritiesEnum[] = [
    UserAuthoritiesEnum.USER,
    UserAuthoritiesEnum.SALE,
    UserAuthoritiesEnum.ADMIN,
    UserAuthoritiesEnum.OFFICE,
  ];

  /** Rolle, die das Backend jedem neu angelegten Nutzer gibt. */
  readonly defaultRole = UserAuthoritiesEnum.USER;

  /**
   * Rollen des Entwurfs in derselben Reihenfolge wie die Admin-Umschalter. Das Backend
   * liefert ein Set, dessen Reihenfolge nicht zugesichert ist — ohne das Sortieren zeigt
   * die Nur-Lese-Ansicht die Badges bei zwei oder mehr Rollen unstabil an. Eine Rolle, die
   * `roleOrder` nicht kennt, wandert ans Ende statt zu verschwinden.
   */
  get draftRolesInOrder(): UserAuthorities[] {
    const rank = (name: UserAuthoritiesEnum): number => {
      const index = this.roleOrder.indexOf(name);
      return index === -1 ? this.roleOrder.length : index;
    };
    return [...(this.userDraft?.authorities ?? [])].sort((a, b) => rank(a.name) - rank(b.name));
  }

  get isUserFormValid(): boolean {
    const d = this.userDraft;
    if (!d) return false;
    // NP20-772: Kein Kuerzel-Kriterium mehr — es kommt vom Backend und ist nicht eingebbar.
    // Auch kein Rollen-Kriterium mehr — beim Anlegen gibt es keine Auswahl, und
    // dass die Basisrolle User erhalten bleibt, setzt das Backend durch.
    // Der Betreuer muss die gewaehlte Firma fuehren: sonst speichert ein Save den Betreuer
    // zurueck, den die Auswahlliste gar nicht anbietet.
    return !!d.firstName?.trim()
      && !!d.lastName?.trim()
      && this.isEmailValid(d.email)
      && !!this.formFirma
      && !!d.superVisor?.id
      && this.advisorsForFirma().some(a => a.id === d.superVisor?.id);
  }

  saveUser(): void {
    if (!this.userDraft || !this.isUserFormValid) return;
    this.userDraft.companyId = this.companyService.byAbbreviation(this.formFirma)?.id ?? null;
    if (this.userModalMode === 'edit') {
      this.saveEditUser(this.userDraft);
    } else {
      this.saveAddUser(this.userDraft);
    }
  }

  private isAbbreviationConflict(err: unknown): boolean {
    if (typeof err !== 'object' || err === null) return false;
    const e = err as { status?: number; error?: { type?: string; errorKey?: string } };
    return e.status === 409 &&
      (!!e.error?.type?.endsWith('abbreviation-exists') || !!e.error?.errorKey?.includes('abbreviation'));
  }

  /**
   * Uebersetzungsschluessel fuer die beiden Rollen-Ablehnungen aus NP20-947,
   * sonst null. 403 kann nur ein manipulierter Client ausloesen — die Oberflaeche bietet
   * Nicht-Admins gar keine Rollenaenderung an.
   *
   * Beide Formen sind gegen das Backend belegt: der 403 traegt allein die Typ-URI
   * (`RoleChangeForbiddenProblem`), der 400 den Schluessel unter `parameters.message`
   * (`BadRequestAlertException`, vom `ExceptionTranslator` verschachtelt durchgereicht).
   * Ein `errorKey` im Body gibt es nicht — der Translator baut das Problem neu auf und
   * laesst die Bean-Property fallen. Eine zweite Verzweigung darauf waere toter Code.
   */
  private roleErrorKeyOf(err: unknown): string | null {
    if (typeof err !== 'object' || err === null) return null;
    const e = err as { status?: number; error?: { type?: string; parameters?: { message?: string } } };
    if (e.status === 403 && e.error?.type?.endsWith('/role-change-forbidden')) {
      return 'adminDash.adminModal.errors.roleChangeForbidden';
    }
    if (e.status === 400 && e.error?.parameters?.message === 'error.userRoleRequired') {
      return 'adminDash.adminModal.errors.userRoleRequired';
    }
    return null;
  }

  addUser() {
    this.openUserModal('add');
  }

  openCompaniesModal(): void {
    this.companiesModalOpen = true;
  }

  onCompaniesModalVisibleChange(open: boolean): void {
    this.companiesModalOpen = open;
  }

  openBuModal(): void {
    this.buModalOpen = true;
  }

  onBuModalVisibleChange(open: boolean): void {
    this.buModalOpen = open;
  }

  onBuSaved(catalog: BusinessUnitDTO[]): void {
    this.businessUnits = catalog;
  }

  openAdvisorsModal(): void {
    this.advisorsModalOpen = true;
  }

  openCostCalculationModal(): void {
    this.costCalculationModalOpen = true;
  }

  onCostCalculationModalVisibleChange(open: boolean): void {
    this.costCalculationModalOpen = open;
  }

  onAdvisorsModalVisibleChange(open: boolean): void {
    this.advisorsModalOpen = open;
  }

  onAdvisorsSaved(advisors: Advisor[]): void {
    this.advisorOptions = [...advisors];
    this.advisorMap.clear();
    advisors.forEach(a => a.id && this.advisorMap.set(a.id, a));
    // Das Advisors-Modal kann User-Zuordnungen umschreiben (Delete-Migration) — die
    // Users-Liste hat davon lokal aber nichts mitbekommen. Frisch laden, damit
    // superVisorId und firma pro Zeile stimmen.
    this.loadAllUsers(this.openOnly ? 'INACTIVE' : undefined);
  }

  onCompaniesSaved(_companies: Company[]): void {
    this.buildCompanyLookup(this.companyService.companies());
    // Firmen-Modal-Save kann sowohl Firmen als auch Advisors umschreiben (Reassignment
    // beim Delete). Advisors frisch holen, dann Users neu laden — nur so stehen die
    // Betreuer-Auswahl und die Firmen-Spalte auf dem frischen Stand.
    loadAllPages<Advisor>((page, size) => this.advisorService.getAdvisors(page, size), 200).subscribe({
      next: advisors => {
        this.advisorOptions = advisors;
        this.advisorMap.clear();
        advisors.forEach(a => a.id && this.advisorMap.set(a.id, a));
        this.loadAllUsers(this.openOnly ? 'INACTIVE' : undefined);
      },
    });
  }

  saveAddUser(newUser: IUser) {
    if (this.isAddingUser) return;
    const trimmedUser: IUser = {
      ...newUser,
      firstName: newUser.firstName?.trim(),
      lastName: newUser.lastName?.trim(),
    };
    this.isAddingUser = true;
    this.adminService
      .register(trimmedUser)
      .pipe(finalize(() => (this.isAddingUser = false)))
      .subscribe({
        next: (createdUser: IUser) => {
          // BE-Response kann Felder wie cost/abbreviation weglassen — die eben
          // abgeschickten Werte hier zurückspielen, damit die Tabelle vollständig ist.
          const created: IUser = {
            ...createdUser,
            cost: createdUser.cost ?? newUser.cost,
            abbreviation: createdUser.abbreviation ?? newUser.abbreviation,
            companyId: createdUser.companyId ?? newUser.companyId,
            superVisor: this.advisorMap.get(createdUser.superVisorId),
          };
          this.users = [
            ...this.users,
            { ...created, firma: this.firmaOf(created) },
          ];
          this.applyFilter();
          this.closeUserModal();
          this.messageService.success('adminDash.adminModal.userCreated');
        },
        error: (err) => {
          const invalidNames = invalidNameFields(err);
          if (isNoFreeAbbreviationError(err)) {
            this.messageService.error(
              'adminDash.adminModal.errors.noFreeAbbreviation',
            );
          } else if (err.status === 409) {
            this.messageService.error(
              'adminDash.adminModal.errors.emailExists',
            );
          } else if (invalidNames.length > 0) {
            invalidNames.forEach((field) =>
              this.messageService.error(INVALID_NAME_MESSAGE_KEYS[field]),
            );
          } else {
            this.messageService.error('general.error');
          }
        },
      });
  }

  deleteUser(user: IUser) {
    this.adminService.deleteUser(user.id).subscribe({
      next: () => {
        this.messageService.success('adminDash.deleteModal.userDeleted');
        this.users = this.users.filter(u => u.id !== user.id);
        this.applyFilter();
      },
      error: () =>
        this.messageService.error('adminDash.deleteModal.userNotDeleted')
    });
  }

  selectDeleteUser(user: IUser) {
    this.selectedUser = {...user};
    this.showDeleteModal = true;
  }

  editUser(user: IUser) {
    this.openUserModal('edit', user);
  }

  saveEditUser(user: IUser) {
    if (!('id' in user)) return;
    const currentPageIndex = this.pageIndex;

    const idx = this.users.findIndex(u => u.id === user.id);
    const oldUser = {...this.users[idx]};
    const targetStatus = user.status;
    const statusChanged = targetStatus !== oldUser.status;

    const applyStatus$ = statusChanged && targetStatus
      ? this.adminService.setStatus({status: targetStatus}, user.id!)
      : of(null);

    applyStatus$.pipe(switchMap(() => this.adminService.updateUser(user))).subscribe({
      next: (updatedUser: IUser) => {
        // BE-updateUser liefert manchmal noch den alten Status zurück — die eben
        // via setStatus gesetzte Zielangabe hat aber Vorrang.
        if (statusChanged && targetStatus) {
          updatedUser = {...updatedUser, status: targetStatus};
        }
        const newUsers = [...this.users];
        // firma ist die Anzeigeform der companyId und liegt nicht im BE-Response —
        // nach dem Save aus der eben gespeicherten Firma neu berechnen, damit die
        // Firma-Spalte nicht auf "Keine Firma" fällt, wenn nur Kosten o.ä. geändert wurden.
        const saved: IUser = {
          ...updatedUser,
          cost: updatedUser.cost ?? user.cost,
          abbreviation: updatedUser.abbreviation ?? user.abbreviation,
          companyId: updatedUser.companyId ?? user.companyId,
          superVisor: this.advisorMap.get(updatedUser.superVisorId),
        };
        newUsers[idx] = {...saved, firma: this.firmaOf(saved)};
        // Filter „Nur offene Benutzer" zeigt nur INACTIVE — ein gerade angenommener
        // User (ACTIVE) gehört dort nicht mehr rein und verschwindet aus der Liste.
        this.users = this.openOnly && updatedUser.status === UserStatusEnum.ACTIVE
          ? newUsers.filter(u => u.id !== updatedUser.id)
          : newUsers;
        this.applyFilter();
        this.pageIndex = currentPageIndex;
        this.closeUserModal();
        this.messageService.success('adminDash.editModal.userUpdated');
      },
      error: (err) => {
        const roleErrorKey = this.roleErrorKeyOf(err);
        if (roleErrorKey) {
          // Modal bleibt mit den Eingaben offen, damit die Rollenauswahl korrigiert werden kann.
          this.messageService.error(roleErrorKey);
        } else if (this.isAbbreviationConflict(err)) {
          this.messageService.error('adminDash.adminModal.errors.abbreviationExists');
        } else if (err.status === 409 && err.error?.parameters?.errorCode === 'DUPLICATE_EMAIL_DATA_INTEGRITY') {
          this.messageService.error('adminDash.adminModal.errors.emailIntegrity');
        } else if (err.status === 409) {
          this.messageService.error('adminDash.adminModal.errors.emailExists');
        } else {
          this.messageService.error('general.error');
        }
        this.users[idx] = oldUser;
      }
    });
  }

  applyFilter(): void {
    const term = this.searchTerm?.toLowerCase().trim();
    this.pageIndex = 0;

    const filtered = this.users.filter((user) => {
      const first = user.firstName?.toLowerCase() ?? '';
      const last = user.lastName?.toLowerCase() ?? '';
      const fullName = `${first} ${last}`.trim();
      const matchesSearch =
        !term ||
        first.includes(term) ||
        last.includes(term) ||
        fullName.includes(term) ||
        (user.abbreviation?.toLowerCase() ?? '').includes(term) ||
        (user.email?.toLowerCase() ?? '').includes(term);
      return matchesSearch && this.matchesCompany(user) && this.matchesRole(user);
    });
    // NP20-917: Sortierung greift auf die gefilterte Liste und vor der Paginierung.
    this.filteredUsers = this.applySort(filtered);
    this.syncSelectionWithFilter();
  }

  private matchesCompany(user: IUser): boolean {
    if (this.selectedCompany === 'ALL') {
      return true;
    }
    if (this.selectedCompany === 'NONE') {
      return !user.firma;
    }
    return this.normalizeAbbr(user.firma) === this.normalizeAbbr(this.selectedCompany);
  }

  onCompanyFilterChange(value: AdminCompanyFilter): void {
    if (this.selectedCompany === value) {
      return;
    }
    this.selectedCompany = value;
    this.applyFilter();
  }

  private matchesRole(user: IUser): boolean {
    if (this.selectedRole === 'ALL') {
      return true;
    }
    return !!user.authorities?.some(authority => authority.name === this.selectedRole);
  }

  onRoleFilterChange(value: AdminRoleFilter): void {
    if (this.selectedRole === value) {
      return;
    }
    this.selectedRole = value;
    this.applyFilter();
  }

  chipStyle(color: string, active: boolean): Record<string, string> {
    if (!active) return {};
    const safe = (color || '').trim();
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(safe)) return {};
    return {
      background: `color-mix(in srgb, ${safe} 12%, white)`,
      color: safe,
      'border-color': `color-mix(in srgb, ${safe} 45%, white)`,
    };
  }

  initialsOf(user: IUser | null | undefined): string {
    const first = (user?.firstName ?? '').trim().charAt(0);
    const last = (user?.lastName ?? '').trim().charAt(0);
    return (first + last).toUpperCase();
  }

  isOpenRegistration(user: IUser | null | undefined): boolean {
    return !!user && user.status !== UserStatusEnum.ACTIVE;
  }

  companyBadgeStyleOf(abbreviation: string | null | undefined): Record<string, string> {
    const color = (this.companyService.byAbbreviation(abbreviation)?.color || '').trim();
    const match = /^#([0-9a-fA-F]{6})$/.exec(color);
    if (!match) return {background: '#F1F2F4', color: '#5F6368', 'border-color': '#DADCE0'};
    const int = parseInt(match[1], 16);
    const r = (int >> 16) & 0xff;
    const g = (int >> 8) & 0xff;
    const b = int & 0xff;
    return {
      background: `rgba(${r}, ${g}, ${b}, 0.12)`,
      color,
      'border-color': `rgba(${r}, ${g}, ${b}, 0.35)`,
    };
  }

  companyNameOf(abbreviation: string | null | undefined): string {
    return this.companyService.byAbbreviation(abbreviation)?.name ?? (abbreviation ?? '');
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
  }

  toggleUserStatus(user: IUser) {
    if (user.status == UserStatusEnum.INACTIVE) {
      user.status = UserStatusEnum.ACTIVE;
    } else if (user.status == UserStatusEnum.ACTIVE) {
      user.status = UserStatusEnum.INACTIVE;
    }
  }

  setUserStatus(user: IUser) {
    this.toggleUserStatus(user);
    let userStatus: UserStatus = {
      status: user.status
    };
    this.adminService.setStatus(userStatus, user.id).subscribe();
  }

  checkStatus(user: IUser) {
    return user.status === UserStatusEnum.ACTIVE;
  }

  hasChanges(): boolean {
    if (!this.selectedUser || this.selectedUser.id == null) return false;

    const idx = this.users.findIndex(u => u.id === this.selectedUser.id);
    if (idx === -1) return false;

    const currentUser = this.users[idx];
    return JSON.stringify(this.selectedUser) !== JSON.stringify(currentUser);
  }

  onInactiveToggle(showInactive: boolean): void {
    this.loadAllUsers(showInactive ? 'INACTIVE' : undefined);
  }

  toggleOpenOnly(): void {
    this.openOnly = !this.openOnly;
    this.onInactiveToggle(this.openOnly);
  }

  // -------- Mehrfachauswahl (Bulk-Zuweisung) --------

  toggleSelectUser(user: IUser, event: Event): void {
    if (!user.id) return;
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.selectedUserIds.add(user.id);
    else this.selectedUserIds.delete(user.id);
    this.dropBulkAdvisorIfUnfit();
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      for (const u of this.filteredUsers) {
        if (u.id) this.selectedUserIds.add(u.id);
      }
    } else {
      for (const u of this.filteredUsers) {
        if (u.id) this.selectedUserIds.delete(u.id);
      }
    }
    this.dropBulkAdvisorIfUnfit();
  }

  /**
   * Die Auswahl aendert die angebotenen Betreuer: kommt eine weitere Firma hinzu, fiele der
   * gewaehlte Betreuer aus der Liste, blieb aber im Modell stehen — "Uebernehmen" haette die
   * Benutzer dann einem Betreuer zugewiesen, der ihre Firma nicht fuehrt.
   */
  private dropBulkAdvisorIfUnfit(): void {
    if (!this.bulkAdvisorId) return;
    if (!this.bulkAdvisorOptions().some(a => a.id === this.bulkAdvisorId)) {
      this.bulkAdvisorId = '';
    }
  }

  isAllSelected(): boolean {
    if (!this.filteredUsers.length) return false;
    return this.filteredUsers.every(u => u.id && this.selectedUserIds.has(u.id));
  }

  private syncSelectionWithFilter(): void {
    const visible = new Set(this.filteredUsers.map(u => u.id).filter((id): id is string => !!id));
    for (const id of [...this.selectedUserIds]) {
      if (!visible.has(id)) this.selectedUserIds.delete(id);
    }
  }

  clearSelection(): void {
    this.selectedUserIds.clear();
    this.bulkAdvisorId = '';
  }

  /**
   * Nur Betreuer, die ALLE markierten Firmen abdecken (every, nicht some) — sonst
   * würden SPT-Benutzer versehentlich einem reinen TSB-Betreuer zugewiesen. Ein
   * Betreuer mehrerer Firmen deckt entsprechend eine gemischte Auswahl ab.
   */
  bulkAdvisorOptions(): Advisor[] {
    const selectedUsers = this.users.filter(u => u.id && this.selectedUserIds.has(u.id));
    const requiredFirmen = [...new Set(selectedUsers.map(u => u.firma).filter((f): f is string => !!f))];
    return this.advisorOptions.filter(a => this.advisorCoversAllFirmen(a, requiredFirmen));
  }

  private advisorCoversAllFirmen(advisor: Advisor, firmen: string[]): boolean {
    if (firmen.length === 0) return true;
    const advisorAbbrs = new Set(
      (advisor.companyIds ?? [])
        .map(id => this.normalizeAbbr(this.companyService.byId(id)?.abbreviation))
        .filter((abbr): abbr is string => !!abbr),
    );
    return firmen.every(f => {
      const abbr = this.normalizeAbbr(f);
      return !!abbr && advisorAbbrs.has(abbr);
    });
  }

  /** Nur Umhaengen: einen Betreuer zu leeren sieht der Backend-Kontrakt nicht vor. */
  applyBulkAdvisor(): void {
    if (!this.bulkAdvisorId) return;
    const targetAdvisor = this.bulkAdvisorOptions().find(a => a.id === this.bulkAdvisorId);
    if (!targetAdvisor?.id) return;

    const targetsFromLocal = this.users.filter(u => u.id && this.selectedUserIds.has(u.id));
    if (!targetsFromLocal.length) return;

    const calls = targetsFromLocal.map(u =>
      this.adminService.reassignSupervisor(u, targetAdvisor.id!),
    );

    forkJoin(calls).subscribe({
      next: () => {
        // Die Firma haengt am Benutzer und bleibt beim Betreuer-Wechsel unberuehrt.
        this.users = this.users.map(u =>
          u.id && this.selectedUserIds.has(u.id)
            ? {...u, superVisor: targetAdvisor, superVisorId: targetAdvisor.id}
            : u,
        );
        this.clearSelection();
        this.applyFilter();
        this.messageService.success('adminDash.userTable.bulk.applied');
      },
      error: () => this.messageService.error('general.error'),
    });
  }

  // -------- Sortierung (NP20-917) --------

  /** Klick oder Enter/Space auf einen Spalten-Header: aufsteigend -> absteigend -> keine Sortierung. */
  onSort(key: AdminSortKey): void {
    if (this.sortKey !== key) {
      this.sortKey = key;
      this.sortDir = 'asc';
    } else if (this.sortDir === 'asc') {
      this.sortDir = 'desc';
    } else {
      this.sortKey = null;
      this.sortDir = 'asc';
    }
    this.applyFilter();
  }

  onSortKeydown(event: KeyboardEvent, key: AdminSortKey): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Space scrollt sonst die Seite, weil der Header nur ein fokussierbares th ist.
    event.preventDefault();
    this.onSort(key);
  }

  /** aria-sort-Wert für den Header (Barrierefreiheit). */
  ariaSort(key: AdminSortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey !== key) return 'none';
    return this.sortDir === 'asc' ? 'ascending' : 'descending';
  }

  sortIndicator(key: AdminSortKey): string {
    return sortIndicatorFor(this.sortKey, key, this.sortDir);
  }

  private applySort(users: IUser[]): IUser[] {
    const key = this.sortKey;
    if (!key) return users;

    const factor = this.sortDir === 'asc' ? 1 : -1;
    return [...users].sort((a, b) => {
      // Leere Werte stehen in beiden Richtungen am Ende, deshalb ohne Richtungs-Faktor.
      const byEmpty = this.compareEmptyLast(a, b, key);
      return byEmpty !== 0 ? byEmpty : this.compareBy(a, b, key) * factor;
    });
  }

  private compareEmptyLast(a: IUser, b: IUser, key: AdminSortKey): number {
    const emptyA = this.isEmptyFor(a, key);
    const emptyB = this.isEmptyFor(b, key);
    if (emptyA === emptyB) return 0;
    return emptyA ? 1 : -1;
  }

  private isEmptyFor(user: IUser, key: AdminSortKey): boolean {
    switch (key) {
      case 'abbreviation': return !user.abbreviation?.trim();
      case 'company':      return !user.firma?.trim();
      case 'advisor':      return !this.advisorName(user);
      case 'cost':         return user.cost == null;
      default:             return false;
    }
  }

  private advisorName(user: IUser): string {
    return `${user.superVisor?.lastName ?? ''}${user.superVisor?.firstName ?? ''}`.trim();
  }

  private compareBy(a: IUser, b: IUser, key: AdminSortKey): number {
    switch (key) {
      case 'user':
        return this.compareName(a.lastName, a.firstName, b.lastName, b.firstName);
      case 'advisor':
        return this.compareName(a.superVisor?.lastName, a.superVisor?.firstName,
          b.superVisor?.lastName, b.superVisor?.firstName);
      case 'abbreviation':
        return this.compareText(a.abbreviation, b.abbreviation);
      case 'email':
        return this.compareText(a.email, b.email);
      case 'company':
        return this.compareText(a.firma, b.firma);
      // Boolean-Spalten: aufsteigend zeigt den positiven Zustand zuerst
      // (verknüpfter Account bzw. angenommene Registrierung).
      case 'account':
        return Number(!a.consultantId) - Number(!b.consultantId);
      case 'accepted':
        return Number(!this.checkStatus(a)) - Number(!this.checkStatus(b));
      case 'cost':
        return (a.cost ?? 0) - (b.cost ?? 0);
    }
  }

  private compareName(lastA?: string, firstA?: string, lastB?: string, firstB?: string): number {
    const byLastName = this.compareText(lastA, lastB);
    return byLastName !== 0 ? byLastName : this.compareText(firstA, firstB);
  }

  /** Locale-aware und case-insensitiv; die Sortier-Sprache folgt der UI-Sprache. */
  private compareText(a?: string | null, b?: string | null): number {
    return (a ?? '').localeCompare(b ?? '', this.translate.currentLang, {sensitivity: 'base'});
  }

  openCvUpload(user: IUser) {
    if (!user.consultantId) return;
    this.router.navigate(['cv-import', user.consultantId], {
      queryParams: {[ADMIN_DASH_RETURN_QUERY_PARAM]: ADMIN_DASH_RETURN_MARKER},
    });
  }
}
